import type { RefObject } from "react";
import { mergeProps, useOverlay, useOverlayPosition, useOverlayTrigger } from "react-aria";
import type { OverlayTriggerState } from "react-stately";

import type { AllOverlayTriggerState, OverlayOptions } from "../types/overlay.d.ts";

/** @deprecated use Modal or PopOver with useOverlay */
export const useOverlayWithPosition = (
  triggerRef: RefObject<Element>,
  overlayRef: RefObject<HTMLElement>,
  state: AllOverlayTriggerState,
  options?: OverlayOptions,
) => {
  // Get props for the trigger and overlay. This also handles
  // hiding the overlay when a parent element of the trigger scrolls
  // (which invalidates the popover positioning).
  const { triggerProps, overlayProps: overlayTriggerProps } = useOverlayTrigger(
    { type: "dialog" },
    state as OverlayTriggerState,
    triggerRef,
  );

  const {
    overlayProps: overlayPositionProps,
    arrowProps,
    placement,
    updatePosition,
  } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef: overlayRef,
    placement: options?.placement,
    offset: options?.offset,
    crossOffset: options?.crossOffset,
    isOpen: state.isOpen,
    containerPadding: options?.containerPadding,
  });

  const { overlayProps, underlayProps } = useOverlay(
    {
      ...options,
      onClose: () => {
        options?.onClose?.();
        state.close();
      },
      isOpen: state.isOpen,
      shouldCloseOnBlur: options?.shouldCloseOnBlur ?? true,
      isKeyboardDismissDisabled: options?.isKeyboardDismissDisabled ?? false,
      isDismissable: options?.isDismissable ?? true,
    },
    overlayRef,
  );

  // arrowProps only returns CSS top and left.
  const arrowStyle = {
    ...arrowProps.style,
    top: placement === "top" ? "100%" : placement === "bottom" ? 0 : arrowProps.style?.top,
    left: placement === "left" ? "100%" : placement === "right" ? 0 : arrowProps.style?.left,
  };

  return {
    triggerProps,
    overlayProps: mergeProps(overlayPositionProps, overlayTriggerProps, overlayProps, {
      "data-placement": placement,
    }),
    underlayProps,
    arrowProps: mergeProps(arrowProps, {
      style: arrowStyle,
      "data-placement": placement,
    }),
    placement,
    updatePosition,
  };
};
